/* =========================================================================
   ATTRIBUTION CAPTURE
   =========================================================================
   Records how a visitor first reached the site and keeps it for 90 days, so an
   enquiry submitted on a later visit still credits the campaign that earned it.

   Without this, a prospect who arrives from a LinkedIn campaign, leaves, and
   returns a fortnight later by typing the domain is recorded as direct traffic
   — every genuine influence win gets filed as a referral.

   First touch is deliberately sticky: it is written once and never overwritten,
   so a later organic visit cannot claim credit for the campaign that did the
   work. Last touch is kept alongside it, refreshed only on visits that actually
   carry campaign parameters.

   Storage is a single first-party cookie. Named parameters only — never the
   full referring URL, which can carry someone's search terms or a session id in
   its own query string.
   ========================================================================= */

const COOKIE = 'ace_attribution';
const MAX_AGE_DAYS = 90;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
const CLICK_IDS = ['gclid', 'gbraid', 'wbraid', 'msclkid', 'fbclid', 'li_fat_id'];
const TRACKED = UTM_KEYS.concat(CLICK_IDS);

function readCookie(name) {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}` +
    `; expires=${expires}; path=/; SameSite=Lax` +
    (location.protocol === 'https:' ? '; Secure' : '');
}

// Reduced to a hostname on purpose — see the note above about referring URLs.
function referrerHost() {
  if (!document.referrer) return '(direct)';
  try {
    const host = new URL(document.referrer).hostname;
    return host === location.hostname ? '(internal)' : host;
  } catch {
    return '(unknown)';
  }
}

function currentTouch() {
  const params = new URLSearchParams(location.search);
  const touch = { referrer: referrerHost(), landing_page: location.pathname };
  for (const key of TRACKED) {
    const value = params.get(key);
    if (value) touch[key] = value.slice(0, 200);
  }
  return touch;
}

function load() {
  const raw = readCookie(COOKIE);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

let stored = null;

/**
 * Fill a form's hidden attribution inputs, creating any that are absent so
 * markup only has to declare the fields a human will actually see.
 */
export function decorateForm(form) {
  if (!form || !stored) return;
  const first = stored.first || {};
  const last = stored.last || {};

  const fields = {
    first_touch_source: first.utm_source || '',
    first_touch_medium: first.utm_medium || '',
    first_touch_campaign: first.utm_campaign || '',
    first_touch_referrer: first.referrer || '',
    first_touch_landing_page: first.landing_page || '',
    first_seen: stored.first_seen || '',
    last_touch_source: last.utm_source || first.utm_source || '',
    last_touch_campaign: last.utm_campaign || first.utm_campaign || '',
    gclid: last.gclid || first.gclid || '',
    submitted_from: location.pathname
  };

  for (const [name, value] of Object.entries(fields)) {
    if (!value) continue;
    let input = form.querySelector(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }
}

export function initAttribution() {
  stored = load();
  const touch = currentTouch();
  const hasCampaign = TRACKED.some(key => Object.prototype.hasOwnProperty.call(touch, key));

  if (!stored) {
    stored = { first: touch, first_seen: new Date().toISOString() };
    writeCookie(COOKIE, JSON.stringify(stored), MAX_AGE_DAYS);
  } else if (hasCampaign) {
    stored.last = touch;
    writeCookie(COOKIE, JSON.stringify(stored), MAX_AGE_DAYS);
  }

  document.querySelectorAll('form[data-attribution]').forEach(decorateForm);
}
